// Phase Q.2 — Per-route response cache for offline-tolerant reads.
//
// Wraps GET endpoints so that on network failure the last successful
// response is served back to the caller with a stale flag. Cache is
// per-shop (uses posDb.shopDb(shop)) so switching shops doesn't leak
// one shop's data into another. Cache substrate is the existing
// IndexedDB wrapper — every entry is a DbRecord with an `expires`
// timestamp, so the substrate handles TTL eviction for us.

import { posDb } from '../database/database';

// Default TTL for cache entries. 24 hours is comfortably longer than
// a normal cafe day; an entry that's been sitting longer than that is
// probably stale enough that the operator should be nudged to reconnect
// before relying on it. Per-route overrides can be added on the
// CacheConfig object if needed.
const DEFAULT_TTL_MS = 24 * 60 * 60 * 1000;

// "Soft stale" — under this age, the entry is served silently. Over
// it, callers can choose to surface a "last synced X ago" badge.
export const SOFT_STALE_MS = 5 * 60 * 1000;

export interface CacheReadResult<T> {
  // The response data the network would have returned.
  data: T;
  // True iff this hit came from cache (and therefore may be stale).
  // False means "we never had to fall back" (caller can treat as
  // fresh).
  fromCache: boolean;
  // When the cache entry was originally fetched, ISO 8601 UTC.
  // Only populated when fromCache=true.
  cachedAtUtc?: string;
}

export interface CachedEnvelope<T> {
  data: T;
  cachedAtUtc: string;
  route: string;
}

// Stable JSON stringification for the cache key — sorted object keys so
// `{a:1,b:2}` and `{b:2,a:1}` hash the same. Cheaper than a full hash
// function for the call volume we expect.
function stableKey(input: unknown): string {
  if (input === null || typeof input !== 'object') return JSON.stringify(input);
  if (Array.isArray(input)) {
    return '[' + input.map(stableKey).join(',') + ']';
  }
  const obj = input as Record<string, unknown>;
  const keys = Object.keys(obj).sort();
  return (
    '{' +
    keys.map((k) => JSON.stringify(k) + ':' + stableKey(obj[k])).join(',') +
    '}'
  );
}

function cacheId(route: string, query?: unknown): string {
  const qKey = query === undefined ? '' : stableKey(query);
  return `cache:${route}${qKey ? ':' + qKey : ''}`;
}

// Resolve which IDB to use for the cache. Per-shop scoping when shopName
// is provided; falls back to master for endpoints without shop context
// (rare — most things are per-shop).
function pickDb(shopName?: string) {
  if (shopName) return posDb.shopDb(shopName);
  return posDb.master;
}

// Try to write a response into the cache. Best-effort — failures
// (QuotaExceeded, IDB closed, etc.) are swallowed; the network response
// is what the caller actually got.
export async function cachePut<T>(
  route: string,
  data: T,
  options?: { shopName?: string; query?: unknown; ttlMs?: number }
): Promise<void> {
  const id = cacheId(route, options?.query);
  const envelope: CachedEnvelope<T> = {
    data,
    cachedAtUtc: new Date().toISOString(),
    route,
  };
  // TTL is enforced on READ (cacheGet returns null when ageMs > ttl)
  // rather than at write time. setRecord doesn't accept an explicit
  // expires hint; rather than threading one through the wrapper, we
  // rely on cachedAtUtc and let the entry sit until evicted, expired,
  // or replaced by a fresher write.
  try {
    const db = pickDb(options?.shopName);
    await db.setRecord(id, envelope);
  } catch {
    // ignore — best-effort
  }
}

// Read a cached response. Returns null if no cache entry exists OR if
// the entry has exceeded its TTL (treated as never-cached so the next
// network failure surfaces appropriately instead of returning week-old
// data silently).
export async function cacheGet<T>(
  route: string,
  options?: { shopName?: string; query?: unknown; ttlMs?: number }
): Promise<CachedEnvelope<T> | null> {
  const id = cacheId(route, options?.query);
  const ttl = options?.ttlMs ?? DEFAULT_TTL_MS;
  try {
    const db = pickDb(options?.shopName);
    const rec = await db.getRecord<CachedEnvelope<T>>(id);
    if (!rec?.value) return null;
    const ageMs = Date.now() - new Date(rec.value.cachedAtUtc).getTime();
    if (ageMs > ttl) return null;
    return rec.value;
  } catch {
    return null;
  }
}

// Evict a specific cache entry. Called by state-changing endpoints
// after they succeed when invalidating one known query shape. Best-
// effort.
export async function cacheEvict(
  route: string,
  options?: { shopName?: string; query?: unknown }
): Promise<void> {
  const id = cacheId(route, options?.query);
  try {
    const db = pickDb(options?.shopName);
    await db.deleteRecord(id);
  } catch {
    // ignore
  }
}

// Evict a known set of query variants for a route. Useful when a
// write affects a route that has query variants (e.g. listSuppliers
// with includeInactive=true/false). Callers list the variants
// explicitly. A future getRange-based prefix evictor would replace
// this when the wrapper grows that primitive (deferred in
// features.md §3.5).
export async function cacheEvictVariants(
  route: string,
  variants: Array<unknown | undefined>,
  options?: { shopName?: string }
): Promise<void> {
  await Promise.all(
    variants.map((q) =>
      cacheEvict(route, { shopName: options?.shopName, query: q })
    )
  );
}

// Wraps a "fetch + cache" round trip. The caller passes:
//   - route: stable string for cache keying ("listMenu", "listStock", ...)
//   - shopName: per-shop scoping
//   - query: optional discriminator for endpoints with query params
//             (e.g. ListDeliveries by status)
//   - fetcher: the actual network call, returns the response payload
//             on success. Throws or returns the rejection sentinel on
//             failure.
//
// Behavior:
//   - On network success: writes to cache, returns { data, fromCache:false }
//   - On network failure: reads cache, returns
//             { data, fromCache:true, cachedAtUtc }
//   - On both fail: rethrows the original network error
export async function fetchWithCache<T>(
  route: string,
  fetcher: () => Promise<T>,
  options?: { shopName?: string; query?: unknown; ttlMs?: number }
): Promise<CacheReadResult<T>> {
  try {
    const data = await fetcher();
    void cachePut(route, data, options);
    return { data, fromCache: false };
  } catch (networkErr) {
    const cached = await cacheGet<T>(route, options);
    if (cached) {
      return {
        data: cached.data,
        fromCache: true,
        cachedAtUtc: cached.cachedAtUtc,
      };
    }
    throw networkErr;
  }
}
